'use client'

/** The header chip naming what the story waits for next, with its Continue button. */

import React from 'react'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import { useStory } from '@/lib/story'
import { usePersona } from '@/lib/persona'
import { useAutopilotWait, type AutopilotWaitText } from '@/lib/autopilot'
import { SUPPORT_PERSONA } from '@/components/data-manager/dm-api'
import { STAGE_MODE_EVENT, getStageMode } from '@/components/presenter/stage-bus'
import { continueStory } from '@/components/presenter/useStoryControls'

/** True when this window is the stage window, or the Presenter window doubling as the stage. */
function useIsStageWindow(): boolean {
  const [stage, setStage] = React.useState(false)
  React.useEffect(() => {
    const read = () => setStage(getStageMode() !== null)
    read()
    window.addEventListener(STAGE_MODE_EVENT, read)
    return () => window.removeEventListener(STAGE_MODE_EVENT, read)
  }, [])
  return stage
}

const PRESENTER_PATH = '/presenter'

/**
 * The chip's Continue button (plans/10 5.1). Satisfies the wait the screen
 * shows and never approves anything. While a press waits for the minimum time
 * it reads "Continuing..." and is aria-disabled (not disabled), so keyboard
 * focus stays on it. `shortcut` is true only where Shift+Alt+Right is Continue
 * for this button (a stage window that is not the Presenter page, which
 * claims the key for its own panel). `onFocusLost` runs when the button goes
 * while it has focus, so the chip can take the focus over.
 */
function ContinueButton({ wait, shortcut, onFocusLost }: { wait: AutopilotWaitText; shortcut: boolean; onFocusLost: () => void }) {
  const [sending, setSending] = React.useState(false)
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const lost = React.useRef(onFocusLost)
  React.useLayoutEffect(() => {
    lost.current = onFocusLost
  })
  // The cleanup runs while the button is still in the page, so it can see whether it held the focus.
  React.useLayoutEffect(() => {
    const button = buttonRef.current
    return () => {
      if (button && document.activeElement === button) lost.current()
    }
  }, [])

  const pending = sending || wait.continued
  const name = wait.nextTitle ? `Continue to ${wait.nextTitle}` : 'Continue'
  const press = async () => {
    if (pending) return
    setSending(true)
    try {
      await continueStory(wait.token, 'chip', wait.nextTitle, 'header')
    } catch {
      // The refusal sentence shows under the chip.
    } finally {
      setSending(false)
    }
  }
  return (
    <Button
      ref={buttonRef}
      type='button'
      variant='outline'
      onClick={() => void press()}
      aria-label={pending ? `Continuing to ${wait.nextTitle ?? 'the next step'}` : name}
      aria-disabled={pending || undefined}
      aria-busy={sending || undefined}
      aria-keyshortcuts={shortcut ? 'Shift+Alt+ArrowRight' : undefined}
      title={pending ? undefined : shortcut ? `${name} (Shift+Alt+Right)` : name}
      className={cn(
        '-my-0.5 -mr-1.5 h-7 shrink-0 gap-0.5 rounded-full px-2.5 text-xs font-medium text-foreground',
        pending && 'cursor-default opacity-60 hover:bg-transparent'
      )}
    >
      {pending ? 'Continuing…' : 'Continue'}
      {!pending && <Icons.chevronRight className='h-3.5 w-3.5' strokeWidth={1.5} aria-hidden='true' />}
    </Button>
  )
}

/** Where focus goes when the whole chip goes while it holds focus: the page's main region (the skip link's target). */
function focusMain(): void {
  document.getElementById('main-content')?.focus({ preventScroll: true })
}

/**
 * What the story waits for next (plans/10 5.1): "Waiting for the Response
 * head to approve WB-001", "Next: Posts sensed" with Continue, "Next: Posts
 * sensed in 9 s" once Continue is pressed, or the cap countdown in its last
 * minute. Continue shows only in the stage window and for Supervity support.
 * Hidden when the autopilot is off or the story is before the earthquake or
 * at the report. Not a live region: the countdown changes every second. The
 * label shrinks first, so the page crumb and the bell keep their room.
 */
export function AutopilotWaitChip() {
  const { story } = useStory()
  const { persona } = usePersona()
  const pathname = usePathname()
  const stageWindow = useIsStageWindow()
  const wait = useAutopilotWait(story)
  if (!wait) return null
  return (
    <WaitChip
      wait={wait}
      mayContinue={stageWindow || persona === SUPPORT_PERSONA}
      shortcut={stageWindow && pathname !== PRESENTER_PATH}
    />
  )
}

function WaitChip({ wait, mayContinue, shortcut }: { wait: AutopilotWaitText; mayContinue: boolean; shortcut: boolean }) {
  const chipRef = React.useRef<HTMLDivElement>(null)
  const showButton = mayContinue && (wait.canContinue || wait.continued)

  // When the step moves to a decision the button goes; the chip takes the focus instead of losing it.
  const takeFocus = React.useRef(false)
  React.useLayoutEffect(() => {
    if (!takeFocus.current) return
    takeFocus.current = false
    chipRef.current?.focus()
  })
  // When the whole chip goes (the report, the autopilot switched off) while it holds focus, focus moves to the page.
  // The cleanup runs before the chip leaves the page, so it can still see where focus is.
  React.useLayoutEffect(() => {
    const chip = chipRef.current
    return () => {
      if (chip && chip.contains(document.activeElement)) focusMain()
    }
  }, [])

  return (
    <div
      ref={chipRef}
      role='group'
      aria-label='Autopilot'
      tabIndex={-1}
      title={wait.text}
      className={cn(
        'hidden min-w-0 shrink items-center gap-1.5 whitespace-nowrap rounded-full border border-hairline bg-card py-1 pl-3 pr-3 sm:inline-flex',
        'text-xs font-medium tabular-nums text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <Icons.hourglass className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} aria-hidden='true' />
      <span className='min-w-0 max-w-36 truncate xl:max-w-72 2xl:max-w-md'>{wait.lead}</span>
      {wait.countdown && (
        <span className={cn('shrink-0', wait.dueReason === 'cap' && 'text-status-at-risk')}>{wait.countdown}</span>
      )}
      {showButton && (
        <ContinueButton
          wait={wait}
          shortcut={shortcut}
          onFocusLost={() => {
            takeFocus.current = true
          }}
        />
      )}
    </div>
  )
}
